import { ref, computed, watch } from 'vue'
import operationRecordAPI from '@/api/operationRecord'

// 操作记录列表：加载 / 分页 / 搜索(简单+高级) / 收藏 / 删除 / 详情
export function useRecordList(options = {}) {
  const isLoading = ref(false)
  const error = ref(null)
  const page = ref(1)
  const itemsPerPage = ref(5)
  const totalRecord = ref(0)
  const pageCount = computed(() => Math.ceil(totalRecord.value / itemsPerPage.value))
  const operationRecords = ref([])

  // 搜索（统一使用条件搜索）
  const showAdvancedSearch = ref(false)
  const advancedSearch = ref({
    operationName: '',
    emoji: '',
    version: '',
    servoVersion: '',
    isFavorited: null
  })

  // 删除确认
  const deleteDialog = ref(false)
  const deleteId = ref(null)

  // 批量删除确认
  const batchDeleteDialog = ref(false)
  const batchDeleteIds = ref([])

  // 详情
  const detailDialog = ref(false)
  const operationDetail = ref(null)
  const isDetailLoading = ref(false)
  const detailError = ref(null)

  const fetchOperationRecords = async () => {
    isLoading.value = true
    error.value = null
    try {
      const selectedTenantId = options.tenantId?.value
      const hasTenantFilter = selectedTenantId !== null
        && selectedTenantId !== undefined
        && selectedTenantId !== ''
      const searchParams = {
        page: hasTenantFilter ? 1 : page.value,
        limit: hasTenantFilter ? 100000 : itemsPerPage.value
      }

      // 统一使用条件搜索
      if (advancedSearch.value.operationName) searchParams.operationName = advancedSearch.value.operationName
      if (advancedSearch.value.emoji) searchParams.emoji = advancedSearch.value.emoji
      if (advancedSearch.value.version) searchParams.version = advancedSearch.value.version
      if (advancedSearch.value.servoVersion) searchParams.servoVersion = advancedSearch.value.servoVersion
      if (advancedSearch.value.isFavorited !== null && advancedSearch.value.isFavorited !== undefined) {
        searchParams.isFavorited = advancedSearch.value.isFavorited
      }

      const data = await operationRecordAPI.getPage(searchParams)
      // 解析 params 字符串为对象
      try {
        operationRecords.value = data.data.list.map(item => ({
          ...item,
          params: JSON.parse(item.params),
          paramsParsed: true
        }))
      } catch (parseErr) {
        console.error('解析params失败:', parseErr)
        operationRecords.value = data.data.list.map(item => ({
          ...item,
          paramsParsed: false
        }))
      }
      totalRecord.value = data.data.total
      if (hasTenantFilter) {
        const tenantRecords = operationRecords.value.filter(
          item => String(item.tenantId) === String(selectedTenantId)
        )
        const startIndex = (page.value - 1) * itemsPerPage.value
        operationRecords.value = tenantRecords.slice(startIndex, startIndex + itemsPerPage.value)
        totalRecord.value = tenantRecords.length
      }
      error.value = null
    } catch (err) {
      error.value = err.message
      console.error('数据获取失败:', err)
    } finally {
      isLoading.value = false
    }
  }

  const resetAdvancedSearch = () => {
    advancedSearch.value = {
      operationName: '',
      emoji: '',
      version: '',
      servoVersion: '',
      isFavorited: null
    }
  }

  watch(page, () => {
    fetchOperationRecords()
  })

  watch(itemsPerPage, () => {
    if (page.value !== 1) {
      page.value = 1
    } else {
      fetchOperationRecords()
    }
  })

  const toggleFavorite = async (id) => {
    const record = operationRecords.value.find(item => item.id === id)
    if (!record) return
    isLoading.value = true
    try {
      await operationRecordAPI.toggleFavorite(id, record.isFavorited)
      await fetchOperationRecords()
    } catch (err) {
      error.value = err.message
      console.error('收藏操作失败:', err)
    } finally {
      isLoading.value = false
    }
  }

  const deleteRecord = (id) => {
    deleteId.value = id
    deleteDialog.value = true
  }
  const confirmDeleteRecord = async () => {
    if (!deleteId.value) return
    isLoading.value = true
    try {
      await operationRecordAPI.delete(deleteId.value)
      await fetchOperationRecords()
    } catch (err) {
      error.value = err.message
      console.error('删除失败:', err)
    } finally {
      isLoading.value = false
      deleteDialog.value = false
      deleteId.value = null
    }
  }
  const cancelDeleteRecord = () => {
    deleteDialog.value = false
    deleteId.value = null
  }

  const batchDeleteRecords = (ids) => {
    if (!ids || ids.length === 0) return
    batchDeleteIds.value = [...ids]
    batchDeleteDialog.value = true
  }
  const confirmBatchDelete = async () => {
    if (!batchDeleteIds.value.length) return
    isLoading.value = true
    try {
      await Promise.all(batchDeleteIds.value.map(id => operationRecordAPI.delete(id)))
      await fetchOperationRecords()
    } catch (err) {
      error.value = err.message
      console.error('批量删除失败:', err)
    } finally {
      isLoading.value = false
      batchDeleteDialog.value = false
      batchDeleteIds.value = []
    }
  }
  const cancelBatchDelete = () => {
    batchDeleteDialog.value = false
    batchDeleteIds.value = []
  }

  const fetchOperationDetail = async (id) => {
    isDetailLoading.value = true
    detailError.value = null
    try {
      const data = await operationRecordAPI.getDetail(id)
      operationDetail.value = data.data
      detailDialog.value = true
    } catch (err) {
      detailError.value = err.message
      console.error('详情获取失败:', err)
    } finally {
      isDetailLoading.value = false
    }
  }

  return {
    // 列表状态
    isLoading, error, page, itemsPerPage, totalRecord, pageCount, operationRecords,
    // 搜索
    showAdvancedSearch, advancedSearch, resetAdvancedSearch,
    // 删除
    deleteDialog, deleteRecord, confirmDeleteRecord, cancelDeleteRecord,
    // 批量删除
    batchDeleteDialog, batchDeleteIds, batchDeleteRecords, confirmBatchDelete, cancelBatchDelete,
    // 详情
    detailDialog, operationDetail, isDetailLoading, detailError, fetchOperationDetail,
    // 动作
    fetchOperationRecords, toggleFavorite
  }
}
